import { Sizes } from 'enums/sizes';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReferentialKey } from 'api/referential';
import { useAppDispatch } from 'app/hooks';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { Pencil } from 'lucide-react';
import { CourtierListItem } from 'api/courtiers';
import { TableAction } from 'components/display/Table/types';
import { BrokerForm } from 'back-office/features/brokers-view/form/BrokerForm';
import { BrokerEditForm } from 'back-office/features/brokers-view/form/BrokerEditForm';
import { useBrokerForm } from 'back-office/features/brokers-view/hooks/useBrokerForm';
import CourtiersView from './CourtiersView';

/**
 * Écran admin des courtiers : la nouvelle liste générique (facettes, tri, colonnes)
 * sur la route /courtiers/admin/liste, avec l'action « Ajouter un courtier » et,
 * par ligne, « Modifier » (même panneau, formulaire en mode édition).
 */
export default function CourtiersAdminScreen() {
  const { openPanel } = usePanel();
  const { createBroker } = useBrokerForm();
  const dispatch = useAppDispatch();

  // The broker forms pick plateformes from the referential: load it here so a
  // direct arrival on this screen does not leave the field without options.
  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([ReferentialKey.Plateforme]));
  }, [dispatch]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((value) => value + 1), []);

  const openAddBrokerForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter un courtier', size: Sizes['4xl'] },
      <BrokerForm onSubmit={createBroker} onSuccess={refresh} />
    );
  }, [openPanel, createBroker, refresh]);

  const openEditBrokerForm = useCallback(
    (broker: CourtierListItem) => {
      openPanel(
        { title: `Modifier le courtier ${broker.name}`, size: Sizes['4xl'] },
        <BrokerEditForm broker={broker} onSuccess={refresh} />
      );
    },
    [openPanel, refresh]
  );

  const rowActions: TableAction<CourtierListItem>[] = useMemo(
    () => [
      {
        label: 'Modifier',
        icon: <Pencil size={16} />,
        variant: 'slate',
        onClick: (broker) => openEditBrokerForm(broker),
      },
    ],
    [openEditBrokerForm]
  );

  return (
    <CourtiersView
      admin
      createConfig={{ label: 'Ajouter un courtier', onClick: openAddBrokerForm }}
      rowActions={rowActions}
      refreshTrigger={refreshTrigger}
    />
  );
}
